import { CommonDialogs, ImageTable } from "@mcleod/common";
import { ChangeEvent, CityState, ClickEvent, Component, Cursor, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DataSourceValidationEvent, DropdownSelectionEvent, ForcedCase, Label, LookupModelSearchEvent, LookupModelSelectionEvent, Tab, Table, TableContentsChangedEvent, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, TableRowExpansionEvent, TableRowModeChangeEvent, Textbox, Toast } from "@mcleod/components";
import { LabelStyles } from "@mcleod/components/src/components/label/LabelStyles";
import { Api, ArrayUtil, CityUtil, FieldUpdateEvent, McLeodClassicIntegration, ModelRow, Navigation, PermissionsUtil, StringUtil } from "@mcleod/core";
import { getDispatchControlBoolean, getDispatchControlValue, ResponsibilityUtil, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { DuplicateId } from "../DuplicateId";
import { ElectronicAdvances } from "../ElectronicAdvances";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { OrderTabView } from "../OrderTabView";
import { WireUtil } from "../WireUtil";
import { AutogenLayoutDriver } from "./autogen/AutogenLayoutDriver";
import { EquipmentAssignments } from "./EquipmentAssignments";
import { EquipmentItem } from "./EquipmentGroupValidation";
import { overrideCommodityQuickInfoTooltip } from "@mcleod/powerbroker";

const viewDriverURL = "lme/dispatch/asset/Driver?mode=update&key=";

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    stateType: 'S',
    zoneType: 'Z',
    locationType: 'L',
    marketAreaType: 'M',
    useDefault: true
}

export class Driver extends AutogenLayoutDriver {
    _enforceRegulatory: boolean = getDispatchControlBoolean("enforce_regulatory");
    resettingGoogleSearch: boolean = false;
    buttonOpenClassic: any;
    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;
    dialogTextboxProps = { forcedCase: ForcedCase.UPPER, tooltip: "Input will be truncated to eight characters" };
    _movementId: string;
    _orderId: string;
    _moveType: string;
    _shouldValidatePayee: boolean;

    override onLoad() {
        this.layoutEquipmentIssued.setParentRowType(EquipmentItem.DRIVER);
        this.sourceDriver.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceDriverAfterGetDataboundValues(row, dataSource);

        this.textPhysicalDueDate.required = this._enforceRegulatory;
        this.textLicenseExpirationDate.required = this._enforceRegulatory;
        this.textMVRDate.required = this._enforceRegulatory;
        this.layoutTableComment.displayCopyOrderFields = false;
        this.validateUserPermissionForWireCode();

        this.textboxHomeLocationId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => event.filter.is_terminal = "Y");
        this.mainDataSource.doBeforeDataSourcePost = this.validateBeforePost.bind(this);
        this.configureEquipAlertColumns();
        this.configureContactsColumns();
        this.labelOrderId.addUnderlineWhenMouseOver();
        this.sourceDriver.addAfterModeChangeListener(async () => {
            if (this.sourceDriver.mode == DataSourceMode.SEARCH) {
                this.panelHeaderDriver.visible = false;
                this.buttonDriverUsers.visible = await ResponsibilityUtil.isFilterEnabled("driver_filter");
            }
            if (this.sourceDriver.mode == DataSourceMode.ADD) {
                this.buttonDriverUsers.visible = false;
            }
            if (this.sourceDriver.mode == DataSourceMode.UPDATE) {
                const controlValue = this.mainDataSource.activeRow.getString("driver_filter_enabled");
                this.buttonDriverUsers.visible = controlValue === "Y";
            }
        });

        this.layoutTableContact.setCarrierContactColumnsVisible(true, true);
        this.textboxDrvrClass.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => event.filter.applies_to = 'D');
        this.setupPerformanceTabset();

        this.checkboxDisableFeasibility.enabled = getDispatchControlBoolean("apply_feasibility");
        this.configureAutoHomeTable();
    }
    configureAutoHomeTable() {
        this.tabAutoHome.visible = getDispatchControlValue("enable_auto_home") === "Y";
    }

    private async validateBeforePost(): Promise<boolean> {
        if (this.mainDataSource.mode === DataSourceMode.ADD) {
            let result: boolean = true;
            if (this.codeGenerationIsActive && this.mainDataSource.activeRow?.isNull("id")) {
                const response = await this.checkAutogen();

                if (response.data[0]?.is_duplicate) {
                    this.handleDuplicateCode(response.data[0]);
                    result = false;
                }
            } else {
                result = await DuplicateId.isValidCode(this.textId, {
                    tableName: "driver",
                    screenName: "Driver",
                    codeGenerationIsActive: this.codeGenerationIsActive,
                    navigateToUrl: viewDriverURL
                });
            }
            return result;
        }
        return true;
    }

    private handleDuplicateCode(duplicateData: { code: string; description?: string }) {
        this.textId.focus();
        this.showDuplicateCodeDialog(duplicateData.code, true);
    }

    configureEquipAlertColumns() {
        const alertTable = this.layoutTableEquipmentAlerts.tableEquipAlerts;
        const columns = alertTable.columns;
        const lastIndex = 3;

        alertTable.clearColumns();
        for (let i = 0; i <= lastIndex; i++) {
            alertTable.addColumn(columns[i], i == 0, i == lastIndex);
        }
    }

    configureContactsColumns() {
        const contactTable = this.layoutTableContact.tableContacts;
        const cols = contactTable.columns;
        contactTable.clearColumns();

        for (let i = 0; i < cols.length; i++) {
            if (cols[i].headingCell.caption == "Title") {
                cols[i].headingCell.caption = "Relationship";
            }
            contactTable.addColumn(cols[i], i == 0, i == cols.length - 1);
        }
    }

    textboxGroupIdBeforeLookupModelSearch(event) {
        event.filter.is_driver = 'Y';
    }

    showDuplicateCodeDialog(code: string, generated: boolean) {
        const generatedString: string = generated ? " generated " : " ";
        CommonDialogs.showInputDialog("Enter a new code", "Duplicate" + generatedString + "code [" + code + "]", this.dialogTextboxProps)
        .then(async (input) => {
            if (input) {
                input = input.substring(0, 8);
                this.textId.text = input;
                this.mainDataSource.activeRow.set("id", input);
                await this.checkManualCode()
                .then((response) => {
                    if (response.data[0]?.is_duplicate) {
                        this.showDuplicateCodeDialog(input, false);
                    }
                });
            } else {
                this.textId.text = null;
                this.mainDataSource.activeRow.set("id", null);
            }
        });
    }

    private getDriverUsersAgencyField(): string {
        return "primary_agency";
    }

    /** This is an event handler for the beforeExecution event of sourceDriver.  It's gross. */
    sourceDriverAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getDriverUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    textboxAssignedToBeforeLookupModelSearch(event) {
        event.filter.web_user_type = 'U';
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.sf.EntryDriver", this.mainDataSource.activeRow.get("id"));
    }

    /** This is an event handler for the onClick event of labelPreassignment. */
    labelPreassignmentOnClick(event: ClickEvent) {
        const dispatchBy = getDispatchControlValue("dispatch_by");
        const equipmentId: string = dispatchBy == "T" ? this.activeRow.get("tractor.id") : this.activeRow.get("driver.id");
        EquipmentAssignments.showSlideout({
            equipmentId: equipmentId,
            tractorId: this.activeRow.get("tractor.id"),
            movementId: this.activeRow.get("movement.id")
        });
    }


    updateProfileParameters() {
        const firstName = !this.activeRow.isNull("first_name") ? this.activeRow.getString("first_name", "") : "";
        const middleInitial = !this.activeRow.isNull("name_mid_initial") ? this.activeRow.getString("name_mid_initial", "") : "";
        const lastName = !this.activeRow.isNull("name") ? this.activeRow.getString("name", "") : "";

        const driverName = [firstName, middleInitial, lastName].map(s => s.trim()).filter(s => !StringUtil.isEmptyString(s)).join(" ");

        if (driverName) {
            (this.layoutProfileTable).driverName = driverName
        }

        (this.layoutProfileTable).parentRowType = "D";
    }

    get movementId(): string { return this._movementId; }

    set movementId(value: string) { this._movementId = value; }

    get moveType(): string { return this._moveType; }

    set moveType(value: string) { this._moveType = value; }

    get orderId(): string { return this._orderId; }

    set orderId(value: string) { this._orderId = value; }

    get shouldValidatePayee(): boolean { return this._shouldValidatePayee; }

    set shouldValidatePayee(value: boolean) { this._shouldValidatePayee = value; }

    sourceDriverAfterExecution(event) {
        this.updateProfileParameters();
        const SEARCH = DataSourceAction.SEARCH;
        const UPDATE = DataSourceAction.UPDATE;
        const ADD = DataSourceAction.ADD;
        const eventMode = event?.getAction();

        const activeRow = this.mainDataSource.activeRow;
        this.layoutDriverFuelCard.driverFullName = WireUtil.fullName(activeRow?.get("first_name"), activeRow?.get("name"));
        this.layoutDriverFuelCard.driverId = activeRow?.get("driver.id");

        if (eventMode == SEARCH || eventMode == UPDATE) {
            if (this.activeRow?.isNull("movement.id")) {
                this.labelOrder.visible = false;
                this.textTractorId.visible = false;
                this.textTrailerId.visible = false;
                this.textboxLastLocation.visible = false;
                this.textboxDriverPtaDate.visible = false;
                this.panelAdditionalInfo.visible = false;
                this.layoutCurrentDispatchStops.visible = false;
                this.labelNoDispatchInfo.visible = true;
            } else {
                this.layoutCurrentDispatchStops.mainDataSource.search({ id: this.activeRow?.get("movement.id") });
            }

            (this.layoutImageTable as ImageTable).populateForRow("D", this.activeRow?.get("id"));
        }

        if (eventMode == UPDATE) {
            this.textId.enabled = false;
            this.textId.required = true;
            this.textTractorId.printable = true;
            this.textTrailerId.printable = true;
            this.textboxLastLocation.printable = true;
            this.textboxDriverPtaDate.printable = true;
        }

        if (eventMode == ADD) {
            this.tabDispatch.visible = false;
            this.textId.enabled = false;
            if (!PermissionsUtil.isUserDeniedLayout("lme/settlement/DrsPayee") && this.activeRow.isNull("payee_id")) {
                CommonDialogs.showYesNo("Add payee record now?").then((isYes) => {
                    if (isYes) {
                        this.makePayeeFromDriver();
                    }
                });
            }
        }

        if (eventMode == SEARCH) {
            if (this.shouldValidatePayee) {
                const activeRow = this.mainDataSource.activeRow;
                const driverCode = activeRow?.get("id");
                const driverName = WireUtil.fullName(activeRow?.get("first_name"), activeRow?.get("name"));
                WireUtil.validateDriverAdvanceIcon(driverCode, driverName)
                .then(response => {
                    const enableButton = response?.enabled_driver_advances;
                    const visibleButton = response?.visible_driver_advances;
                    this.buttonDriverAdvance.cursor = enableButton ? Cursor.POINTER : null;
                    this.buttonDriverAdvance.tooltip = response?.tooltip_message;
                    this.buttonDriverAdvance.visible = visibleButton;
                    this.buttonDriverAdvance.enabled = enableButton;
                });
            }
        }
        this.retrieveOrderAndMovementId();
        this.buttonMakePayee.enabled = this.activeRow.isNull("payee_id");
        this.buttonMakePayee.disabledTooltip = "Payee already assigned to driver";

        const autoHomeQuickRow = this.tableAutoHome.quickAddRow as TableRow;
        if (autoHomeQuickRow && !this.isQuickAddRowPopulated(autoHomeQuickRow)) {
            this.populateAutoHomeQuickRowFromDriver(autoHomeQuickRow, autoHomeQuickRow.findComponentById("citystateCityId") as CityState);
        }

        if (!activeRow?.isNull("trailer2.id") || !activeRow?.isNull("trailer3.id")) {
            this.textTrailerId.caption = "Trailer 1";
        }

        if (!activeRow?.isNull("dolly1.id") && !activeRow?.isNull("dolly2.id")) {
            this.textDolly1Id.caption = "Dolly 1";
        }

    }

    private validateUserPermissionForWireCode() {

        const isUserAccessDenied = WireUtil.isUserDeniedAccessToLayout();
        if (isUserAccessDenied) {
            this.buttonDriverAdvance.visible = false;
            this.shouldValidatePayee = false;
            return;
        }

        const endpoint = "lme/dispatch/user-permission-wirecode";
        Api.search(endpoint).then(response => {
            const data = response.data[0];
            const enabledDriverAdvances = data?.enabled_driver_advances;
            this.buttonDriverAdvance.cursor = enabledDriverAdvances ? Cursor.POINTER : null;
            const visibleDriverAdvances = data?.visible_driver_advances;
            const tooltipMessage = data?.tooltip_message;
            this.buttonDriverAdvance.visible = this.mainDataSource.mode === DataSourceMode.UPDATE && visibleDriverAdvances;
            this.buttonDriverAdvance.tooltip = tooltipMessage;
            this.buttonDriverAdvance.enabled = enabledDriverAdvances;
            this.shouldValidatePayee = enabledDriverAdvances && visibleDriverAdvances;
        });
    }

    private async retrieveOrderAndMovementId() {
        const activeRow = this.mainDataSource.activeRow;
        const driverCode = activeRow?.get("id");
        const response = await WireUtil.driverOrderAndMovements(driverCode);
        this.movementId = response?.movement_id;
        this.orderId = response?.order_id;
        this.moveType = response?.move_type;
    }

    buttonDriverAdvanceOnClick(event: ClickEvent) {
        const activeRow = this.mainDataSource.activeRow;
        const driverId = activeRow.get("id");
        const options = {
            movementId: this.movementId,
            movementBrokerage: false,
            orderId: this.orderId,
            driverId: driverId,
            driverName: WireUtil.fullName(activeRow?.get("first_name"), activeRow?.get("name")),
            validatedFrom: "Master Driver",
            issueAdvanceCallback: undefined,
            moveType: this.moveType
        };
        ElectronicAdvances.showSlider(options);
    }

    sourceDriverBeforeModeChange(event) {
        this.buttonOpenClassic.visible = event.newMode === DataSourceMode.UPDATE;
        this.tabDispatch.visible = event.newMode !== DataSourceMode.ADD;

        if (event.newMode === DataSourceMode.ADD) {
            this.textId.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "driver" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                if (this.codeGenerationIsActive) {
                    this.textId.caption = "*Driver Code";
                    this.textId.enabledDuringAdd = this.allowManualEntry;
                }
            });
        }

        if (event.newMode === DataSourceMode.UPDATE) {
            this.textId.required = true;
        }

        if (event.newMode === DataSourceMode.SEARCH) {
            this.textboxLastLocation.visible = false;
            this.textboxDriverPtaDate.visible = false;
            this.panelAdditionalInfo.visible = false;
            this.panelCurrentDispatchSearch.visible = true;
            this.layoutCurrentDispatchStops.visible = false;
        }
    }

    async checkAutogen(): Promise<any> {
        return await Api.post("lme/dispatch/check-manual-autogen",
            {
                "table_name": "driver",
                "table_row": this.mainDataSource?.activeRow?.data
            });
    }

    async checkManualCode(): Promise<any> {
        return await Api.post("lme/dispatch/asset/check-manual-driver-code",
            {
                "id": this.mainDataSource?.activeRow?.get("id")
            });
    }

    private duplicateDriverLinkOnClick(driverCode: string) {
        Driver.navigateTo(driverCode, true);
    }


    public static navigateTo(id: string, newTab = true): void {
        Navigation.navigateTo(viewDriverURL + id, { newTab: newTab });
    }

    /** This is an event handler for the afterModeChange event of sourceDriver. */
    sourceDriverAfterModeChange(event: DataSourceModeChangeEvent) {
        this.tabProfiles.visible = event.newMode != DataSourceMode.ADD;
        this.tabImage.visible = this.tabDispatch.visible = event.newMode == DataSourceMode.UPDATE;
        if (event.newMode === DataSourceMode.UPDATE) {
            this.buttonMakePayee.visible = !PermissionsUtil.isUserDeniedLayout("lme/settlement/DrsPayee");
            this.activeRow.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
                this.buttonMakePayee.enabled = !this.activeRow.hasChanged();
            });
        }
    }

    async buttonMakePayeeOnClick(event: ClickEvent) {
        this.makePayeeFromDriver();
    }

    async makePayeeFromDriver() {
        this.buttonMakePayee.busy = true;
        const driverId = this.activeRow.get("id");
        try {
            await Api.post("lme/dispatch/asset/make-driver-payee", {
                driver_id: driverId
            });
            Toast.showSuccessToast(`Payee record created successfully.`);

            // Refresh the driver data to show the updated payee_id
            this.mainDataSource.search({ id: driverId });
            Navigation.navigateTo(`lme/settlement/DrsPayee?mode=update&key=${driverId}`, { newTab: true });
        } finally {
            this.buttonMakePayee.busy = false;
        }
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = this.activeRow?.get("movement_order.order_id");
        OrderTabView.navigateTo({ key: orderId, newTab: true })
    }

    /** This is an event handler for the onClick event of buttonDriverUsers. */
    buttonDriverUsersOnClick(event: ClickEvent) {
        const urs = UsersResponsibilitySlideoutMF.builder()
            .layoutName("lme/dispatch/asset/DriverUsers")
            .title(this.getDriverUsersSlideoutTitle())
            .idFilter(this.getDriverUsersIdFilter())
            .parentMainDataSource(this.sourceDriver)
            .parentAgencyLinkDataSource(this.sourceAgencyLink)
            .primaryAgencyField(this.getDriverUsersAgencyField())
            .build();
        urs.show();
    }

    private getDriverUsersSlideoutTitle(): string {
        const name = this.sourceDriver.activeRow?.get("full_name");
        return "Driver Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getDriverUsersIdFilter(): any {
        const id = this.sourceDriver.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private styleYesNoLabels(tableRow: TableRow, ...labelIds: string[]) {
        for (const labelId of labelIds) {
            const modelRow = tableRow.data as ModelRow;
            const label = tableRow.findComponentById(labelId) as Label;
            if (label && modelRow) {
                label.backgroundColor = modelRow.getBoolean(label.field, false) ? "success" : "error";
            }
        }
    }

    private styleOrderHyperlink(tableRow: TableRow, compId: string) {
        const comp = tableRow.findComponentById(compId) as Label;
        const modelRow = tableRow.data as ModelRow;
        const orderId = modelRow.get(comp.field);

        if (orderId == null) return;
        comp.addMouseEnterListener(event => {
            comp.underlineWhenMouseOver(event.target, true);
            comp.setClassIncluded(LabelStyles.link, true);
        });
        comp.addMouseLeaveListener(event => {
            comp.underlineWhenMouseOver(event.target, false);
            comp.setClassIncluded(LabelStyles.link, false);
        });
        comp.addClickListener((event: ClickEvent) => {
            if (!StringUtil.isEmptyString(orderId)) {
                OrderTabView.navigateTo({ key: orderId, newTab: true });
            }
        })
        comp.tooltip = "Click to view order";
    }

    styleTab(tab: Tab, hasData: boolean) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = hasData ? "primary" : "default.light";
            tab.heading.fontBold = hasData;
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "(0 Results)", fillRow: true,
                    color: tab.heading.color, rowBreak: false, marginLeft: 5
                });
                tab.headingLabel.fillRow = false;
                tab.headingLabel.minWidth = 125;
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    setupPerformanceTabset() {
        if (!this.tabPerformance || !this.tabPerformance.visible)
            return;
        this.tabsetPerformance?.tabs?.forEach((tab: Tab) => {
            const table = tab.components[0];
            if (table && table instanceof Table) {
                const datasource = table.dataSource;
                if (!datasource) return;
                this.styleTab(tab, !datasource.isEmpty())
                datasource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
                    this.sourceAfterExecutionStyleTab(event, tab, table);
                })
            }
        });
    }

    sourceAfterExecutionStyleTab(event: DataSourceExecutionEvent, tab: Tab, table: Table) {
        const hasData: boolean = !(event.target as DataSource).isEmpty();
        this.styleTab(tab, hasData);
        this.updateTabCountLabel(tab, table);
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            const resultText = table.dataSource.rowCount === 1 ? " Result" : " Results";
            tab["countLabel"].text = `(${table.dataSource.rowCount} ${resultText})` + "";
            tab["countLabel"].color = tab.heading.color;
        }
    }

    // Service Incident
    tableServiceIncidentOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.styleOrderHyperlink(tableRow, "labelSfOrderId");
    }

    // OSD
    tableOSDOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.styleYesNoLabels(tableRow, "labelOsdPreventable");
        this.styleOrderHyperlink(tableRow, "labelOsdOrderId");
    }

    // Claims
    tableClaimsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.styleYesNoLabels(tableRow, "labelClaimPreventable");
        this.styleOrderHyperlink(tableRow, "labelClaimOrderId");
    }

    // Accidents
    tableAccidentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        this.styleYesNoLabels(tableRow, "labelAccPreventable");
        tableRow.expandable = !modelRow.isNull("injuries") && modelRow.get("injuries").length > 0;
    }

    tableAccidentsOnRowExpand(event: TableRowExpansionEvent) {
        const injuriesTable = event.expandComponents[0];
        const rowData = event.getTableRow()?.data as ModelRow;

        if (injuriesTable && rowData.get("injuries")) {
            injuriesTable.dataSource = new DataSource();
            injuriesTable.dataSource.setRowsAndMode(DataSourceMode.NONE, rowData.get("injuries")?.map(data =>
                new ModelRow(null, false, data)) ?? []);
        }
    }

    /** This is an event handler for the onRowDisplay event of tableLaneExclusions. */
    tableLaneExclusionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const textboxOriginType = tableRow.findComponentById("textboxOriginType") as Textbox;
        const textboxOrigin = tableRow.findComponentById("textboxOrigin") as Textbox;
        const textboxDestinationType = tableRow.findComponentById("textboxDestinationType") as Textbox;
        const textboxDestination = tableRow.findComponentById("textboxDestination") as Textbox;
        if (textboxOriginType && textboxOrigin) {
            LocationByTypeUtil.setupLocationTypeChangeListener(textboxOriginType, textboxOrigin, LOCATION_BY_TYPE_PROPS);
        }
        if (textboxDestinationType && textboxDestination) {
            LocationByTypeUtil.setupLocationTypeChangeListener(textboxDestinationType, textboxDestination, LOCATION_BY_TYPE_PROPS);
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxFleetManager. */
    textboxFleetManagerBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    textboxTerminationDateOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            this.textboxReasonForLeaving.enabledDuringUpdate = this.textboxReasonForLeaving.enabledDuringAdd =
                this.switchRehire.enabledDuringUpdate = this.switchRehire.enabledDuringAdd = !StringUtil.isEmptyString(event.newValue);
        }
    }

    switchHazmatCertOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            this.textboxHazmatDate.enabledDuringUpdate = this.textboxHazmatDate.enabledDuringAdd = event.newValue;
        }
    }

    switchMedicalExemptOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            this.textboxMedicalCertExpire.enabledDuringUpdate = this.textboxMedicalCertExpire.enabledDuringAdd = !event.newValue;
        }
    }

    /** This is an event handler for the onChange event of switchDisableLoadMatch. */
    switchDisableLoadMatchOnChange(event: ChangeEvent) {
        const switchValue = !this.switchDisableLoadMatch.getBasicValue();
        this.panelLoadMatch.visible = switchValue;
        this.panelLoadMatch.preventCollapse = !switchValue;
    }

    switchMpwExcludeOnChange(event: ChangeEvent) {
        const switchValue = this.switchMpwExclude.getBasicValue();
        this.panelLogixSolution.components.forEach(comp => {
            if (comp != this.switchMpwExclude) {
                comp.enabled = !switchValue;
            }
        })
    }

    /** This is an event handler for the onContentsChanged event of tableDriverMiles. */
    tableDriverMilesOnContentsChanged(event: TableContentsChangedEvent) {
        const hasData: boolean = !(event.getTable().dataSource).isEmpty();
        const tab = event.getTable().parent as Tab;
        this.styleTab(tab, hasData);
        this.updateTabCountLabel(tab, event.getTable());
    }

    /** This is an event handler for the onRowDisplay event of tableAutoHome. */
    tableAutoHomeOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const locationComp = tableRow.findComponentById("textboxLocationId") as Textbox;
        locationComp.addBeforeLookupModelSearchListener((searchEvent: LookupModelSearchEvent) => {
            searchEvent.filter.updating_stop = true;
        });
        locationComp.addLookupModelSelectionListener((selectionEvent: LookupModelSelectionEvent) => {
            if (selectionEvent.hasSelection() === true) {
                this.setLocationTextAfterLocation(selectionEvent, tableRow);
            }
        });
        const cityStateTextbox = tableRow.findComponentById("citystateCityId") as CityState;
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            cityStateTextbox.required = false;
        }
        cityStateTextbox.textCombined.addLookupModelSelectionListener((selectionEvent: LookupModelSelectionEvent) => {
            if (selectionEvent.hasSelection() === true) {
                this.setAutoHomeCityData(selectionEvent, tableRow);
            }
        });
        if (tableRow.isQuickAddRow()) {
            tableRow.data?.set("type_of", "H");
            tableRow.data?.set("radius", getDispatchControlValue("auto_home_radius"));
            tableRow.data?.set("is_active", "Y");

            if (!this.isQuickAddRowPopulated(tableRow)) {
                this.populateAutoHomeQuickRowFromDriver(tableRow, cityStateTextbox);
            }
        }
    }
    setAutoHomeCityData(event: LookupModelSelectionEvent, tableRow: TableRow) {
        const data = event.target.getFirstLookupModelData()?.data;
        if (!data) return;
        (tableRow.findComponentById("textboxLatitude") as Textbox).text = data?.["latitude"];
        (tableRow.findComponentById("textboxLongitude") as Textbox).text = data?.["longitude"];
    }

    setLocationTextAfterLocation(event: any, tableRow: TableRow) {
        const rowData = tableRow.data
        const lookupModelRow = event.target.getFirstLookupModelData() as ModelRow;
        if (!lookupModelRow) return;
        const source = lookupModelRow.get("source_name");
        const cityState = tableRow.findComponentById("citystateCityId") as CityState;
        if (source == "CITY") {
            cityState.textCombined.text =
                CityUtil.formatCityStateZip(lookupModelRow?.get("city_name"), lookupModelRow?.get("state"), lookupModelRow?.get("zip_code"));
        } else {
            (tableRow.findComponentById("textboxAddress") as Textbox).text = lookupModelRow?.get("address1");
            (tableRow.findComponentById("textboxLatitude") as Textbox).text = lookupModelRow?.get("latitude");
            (tableRow.findComponentById("textboxLongitude") as Textbox).text = lookupModelRow?.get("longitude");
            cityState.textCombined.text =
                CityUtil.formatCityStateZip(lookupModelRow?.get("city_name"), lookupModelRow?.get("state"), lookupModelRow?.get("zip_code"));
            if (lookupModelRow?.get("id")) {
                rowData.setValueAndLookupModelData("location_id", lookupModelRow?.get("id"), lookupModelRow);
            }
            rowData.setValueAndLookupModelData("city_id", lookupModelRow?.get("city_id"), lookupModelRow);
            rowData?.set("city_name", lookupModelRow?.get("city_name"));
            rowData?.set("state", lookupModelRow?.get("state"));
            rowData?.set("zip_code", lookupModelRow?.get("zip_code"));
            cityState.displayComponentData(rowData, null, 0);
        }
    }

    private isQuickAddRowPopulated(tableRow: TableRow): boolean {
        // Check if key fields are already populated to determine if we've already initialized this row
        return !StringUtil.isEmptyString(tableRow.data?.get("city_name")) ||
               !StringUtil.isEmptyString((tableRow.findComponentById("textboxLatitude") as Textbox).text);
    }

    private populateAutoHomeQuickRowFromDriver(tableRow: TableRow, cityStateTextbox: CityState) {
        const active = this.mainDataSource.activeRow;
        if (!active) return;
        tableRow.data?.set("city_name", active.get("city"));
        tableRow.data?.set("state", active.get("state"));
        tableRow.data?.set("zip_code", active.get("zip"));
        tableRow.data?.set("city_id", active.get("city_id"));
        tableRow.data?.set("latitude", active.get("latitude"));
        tableRow.data?.set("longitude", active.get("longitude"));
        (tableRow.findComponentById("textboxLatitude") as Textbox).text = active.get("latitude");
        (tableRow.findComponentById("textboxLongitude") as Textbox).text = active.get("longitude");
        cityStateTextbox.displayComponentData(tableRow.data, null, 0);
    }

    /** This is an event handler for the onRowExpand event of tableAutoHomeHistory. */
    tableAutoHomeHistoryOnRowExpand(event: TableRowExpansionEvent) {
        const mcPositionTable = event.expandComponents[1] as Table;
        const rowData = event.getTableRow()?.data as ModelRow;
        mcPositionTable.dataSource = new DataSource(); // if no mc_positions, clear out any existing data lingering from history results
        mcPositionTable.emptyCaptionBeforeSearch = "No results found.";
        if (rowData.get("mc_positions")) {
            mcPositionTable.dataSource.setRowsAndMode(DataSourceMode.NONE, rowData.get("mc_positions")?.map(data =>
                new ModelRow(null, false, data)) ?? []);
        }
    }

    textboxOriginTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        
        const originType = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(originType);
        const origin = tableRow.findComponentById("textboxOrigin") as Textbox;
        const destinationType = tableRow.findComponentById("textboxDestinationType") as Textbox;
        if (event.selection) {
            origin.enabled = true;
            origin.required = true;
            originType.required = false;
            destinationType.required = false;
        }
        else {
            if(tableRow.mode === "update" && !destinationType.required) {
                originType.required = true;
                destinationType.required = true;
            }
            origin.enabled = false;
            origin.required = false;
            tableRow.data?.set("origin", "");
        }
    }

    textboxDestinationTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        const destinationType = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(destinationType);
        const destination = tableRow.findComponentById("textboxDestination") as Textbox;
        const originType = tableRow.findComponentById("textboxOriginType") as Textbox;
        if (event.selection) {
            destination.enabled = true;
            destination.required = true;
            originType.required = false;
            destinationType.required = false;
        }
        else {
            if(tableRow.mode === "update" && !originType.required) {
                originType.required = true;
                destinationType.required = true;
            }
            destination.enabled = false;
            destination.required = false;
            tableRow.data?.set("destination", "");
        }
    }

    //used to enable the origin and destination textboxes when editing existing rows
    tableLaneExclusionsOnRowModeChange(event: TableRowModeChangeEvent) {
        if(event.newMode == "update") {
            const tableRow = event.getTableRow();
            const originType = tableRow.findComponentById("textboxOriginType") as Textbox;
            const origin = tableRow.findComponentById("textboxOrigin") as Textbox;
            const destinationType = tableRow.findComponentById("textboxDestinationType") as Textbox;
            const destination = tableRow.findComponentById("textboxDestination") as Textbox;
            if (originType.text != "") {
                origin.enabled = true;
                origin.required = true;
            }
            else {
                origin.enabled = false;
                origin.required = false;
                tableRow.data?.set("origin", "");
            }
            if (destinationType.text != "") {
                destination.enabled = true;
                destination.required = true;
            }
            else {
                destination.enabled = false;
                destination.required = false;
                tableRow.data?.set("destination", "");
            }
        }
    }

    tableLaneExclusionsBeforeRowSave(event: TableRowBeforeSaveEvent) {
        //if (this.dataSource.mode != DataSourceMode.SEARCH) {
            const tableRow = event.getTableRow();
            const originType = tableRow.findComponentById("textboxOriginType") as Textbox;
            const destinationType = tableRow.findComponentById("textboxDestinationType") as Textbox;
            
            // At least one of origin or destination must be selected
            if (originType.selectedItem == null && destinationType.selectedItem == null) {
                originType.required = true;
                originType.validate(true, true);
                destinationType.required = true;
                destinationType.validate(true, true);
                CommonDialogs.showError("Please select at least one origin or destination type before adding a lane exclusion.");
                return;
            }
            
            // Clear required state if validation passes
            originType.required = false;
            destinationType.required = false;
        //}
    }

    sourceDriverLaneOnValidate(event: DataSourceValidationEvent) {
        if (event.dataSource.mode != DataSourceMode.SEARCH) {
            const tableRows = this.tableLaneExclusions.rows;
            for(const tableRow of tableRows) {
                const originType = tableRow.findComponentById("textboxOriginType") as Textbox;
                const destinationType = tableRow.findComponentById("textboxDestinationType") as Textbox;
                if (originType.selectedItem == null && destinationType.selectedItem == null) {
                    originType.required = true;
                    originType.validate(true, true);
                    destinationType.required = true;
                    destinationType.validate(true, true);
                    event.stop();
                        event.validationResults.push({
                            isValid: false,
                            component: originType,
                            validationMessage: "The origin and destination types cannot both be empty for Load Match."
                        });
                    return;
                }
            }
        }
    }
}